import { useEffect, useRef } from 'react';
import { signIn, signOut, useSession } from 'next-auth/react';
import router from 'next/router';

import { useUserContext } from '../../contexts/UserContext';

import { getCurrentTimeInSeconds } from '../../utils/Time';
import { userLogout } from '../../helpers/Logout';
import { isProtectedRoute } from '../../constants/protectedRoutes';
import { ENVIRONMENT_NAME } from '../../utils';
import { PAGE } from '../../helpers/pages';
import { randomStringGenerator } from '../../helpers/randomStringGenerator';
import { generateDDRecommendedUserCookie } from '../../utils/ddRecommendedUserCookie';
import { NEXT_AUTH_REFRESH_ACCESS_TOKEN_ERROR_MESSAGE } from '../../helpers/constants';
import { logger } from '@dist-marketplace-frontend/services';

const RefreshTokenHandler = () => {
  const { data: session, status, update: updateSession } = useSession();

  const {
    dispatchCookieBasedUser,
    cookieBasedUser,
    consentOptions: { functionalConsent },
  } = useUserContext();

  // Use a ref to remove username from the effect's dependency array
  // as this doesn't make sense to re-trigger the effect when it changes
  // When using React 19 we can replace this with a useEffectEvent
  const usernameRef = useRef(cookieBasedUser?.name || '');
  usernameRef.current = cookieBasedUser?.name || '';

  useEffect(() => {
    let refreshSessionTimeoutId: NodeJS.Timeout | undefined;

    if (status !== 'authenticated') {
      return;
    }

    handleRefreshSessionOnFocusedTab();

    document.addEventListener(
      'visibilitychange',
      handleRefreshSessionOnFocusedTab,
    );

    return () => {
      document.removeEventListener(
        'visibilitychange',
        handleRefreshSessionOnFocusedTab,
      );
      if (refreshSessionTimeoutId) {
        clearTimeout(refreshSessionTimeoutId);
        refreshSessionTimeoutId = undefined;
      }
    };

    function handleRefreshSessionOnFocusedTab() {
      switch (document.visibilityState) {
        case 'visible':
          refreshSessionWhenExpired();
          break;
        case 'hidden':
          if (refreshSessionTimeoutId) {
            clearTimeout(refreshSessionTimeoutId);
            refreshSessionTimeoutId = undefined;
          }
          break;
      }
    }

    function refreshSessionWhenExpired() {
      const currentTimeInSeconds = getCurrentTimeInSeconds();

      if (typeof session?.expiresAt === 'number') {
        const timeRemaining = session.expiresAt - currentTimeInSeconds;

        if (timeRemaining <= 0) {
          updateSession();
        } else {
          refreshSessionTimeoutId = setTimeout(() => {
            updateSession();
          }, timeRemaining * 1000);
        }
      }
    }
  }, [updateSession, session?.expiresAt, status]);

  useEffect(() => {
    if (status !== 'authenticated') {
      return;
    }

    if (tokenHasAnError()) {
      if (isProtectedRoute(location.pathname)) {
        signIn('keycloak');
      } else {
        runOnceAcrossTabs({
          callback: async () => {
            await handleSignOut();
          },
          lockName: 'dd-logout',
        });
      }
    }

    async function handleSignOut() {
      try {
        await Promise.all([handleLegacyLogout(), signOut({ redirect: false })]);
      } catch (error) {
        logger.error({
          message:
            'An error occurred while trying to force the user to log out',
          error,
        });
      }

      async function handleLegacyLogout() {
        if (functionalConsent) {
          document.cookie = generateDDRecommendedUserCookie(
            randomStringGenerator(),
          );
        }
        if (ENVIRONMENT_NAME === 'dev') {
          router.push(`/${PAGE.LOCAL_DEV_SIGNOUT}`);
        } else {
          await userLogout(dispatchCookieBasedUser, usernameRef.current);
        }
      }
    }

    function tokenHasAnError() {
      return session?.error === NEXT_AUTH_REFRESH_ACCESS_TOKEN_ERROR_MESSAGE;
    }
  }, [dispatchCookieBasedUser, session?.error, status, functionalConsent]);

  return null;
};

// NB: This won't work in Safari <= 15.4, it will fallback to running the callback on every tab
function runOnceAcrossTabs({
  lockName,
  callback,
}: {
  lockName: string;
  callback: () => void | Promise<void>;
}) {
  if (!navigator.locks) {
    return callback();
  }

  // ifAvailable: true means that we get lock === null if the lock is already taken by another tab
  // the default behaviour being to wait for the lock to be available
  return navigator.locks.request(
    lockName,
    { ifAvailable: true },
    async (lock) => {
      if (lock === null) {
        return undefined;
      }

      return callback();
    },
  );
}

export default RefreshTokenHandler;
